import React, { useEffect, useRef, useState } from 'react';
import { ConfiguratorGroupDto } from '@/services/configurator';

/**
 * Tracks which group's option view is open. Resets only when the active group genuinely leaves the
 * list (e.g. a room switch) — NOT on the data refetch that follows selecting an option (same groups,
 * new identity), which would otherwise snap the view back to the group list.
 */
export const useActiveGroup = (
    groups: ConfiguratorGroupDto[],
    onGroupExpand?: (group: ConfiguratorGroupDto) => void
) => {
    const [activeGroupId, setActiveGroupId] = useState<number | null>(null);

    useEffect(() => {
        if (activeGroupId != null && !groups.some((g) => g.id === activeGroupId)) {
            setActiveGroupId(null);
        }
    }, [groups, activeGroupId]);

    const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null;

    // Closing an option detail brings the group list/grid back *under the pointer* — in the card
    // layout it's even still mounted behind the panel. On touch, the tap that hit Close emits a
    // follow-up synthesized click ~300ms later, which then lands on whichever group row/card now
    // occupies that spot and silently re-opens it. Arm a brief window on close; the list container
    // swallows clicks during it via `swallowGhostClick` (capture phase, so it runs before the rows'
    // own handlers). Mirrors the same guard on the mobile action bar (MobileConfiguratorActions).
    const suppressUntilRef = useRef(0);
    const swallowGhostClick = (e: React.MouseEvent) => {
        if (Date.now() < suppressUntilRef.current) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const openGroup = (group: ConfiguratorGroupDto) => {
        setActiveGroupId(group.id);
        onGroupExpand?.(group);
    };
    const closeGroup = () => {
        suppressUntilRef.current = Date.now() + 400;
        setActiveGroupId(null);
    };

    return { activeGroup, activeGroupId, openGroup, closeGroup, swallowGhostClick };
};
